/**
 * 同步事件与状态类型（§25 / §27 语义）。
 *
 * payload 直接携带 §24 领域实体（@jilian/shared 类型），事件本身只描述
 * “哪条实体、怎么变更”；服务端按冲突规则应用，不做整份 JSON 覆盖。
 */

import type {
  BodyMeasurement,
  DailyIntent,
  InjuryConstraint,
  MealEntry,
  NotificationSetting,
  ProgressionDecision,
  ReadinessCheck,
  RecoveryCheck,
  TrainingTemplate,
  UserProfile,
  WorkoutExercise,
  WorkoutPlan,
  WorkoutSession,
  WorkoutSet,
} from "@jilian/shared";

/** 参与同步的实体类型（§24 核心实体；exercises 只读除外）。 */
export type SyncEntityType =
  | "user_profile"
  | "injury_constraint"
  | "workout_plan"
  | "workout_session"
  | "workout_exercise"
  | "workout_set"
  | "readiness_check"
  | "recovery_check"
  | "progression_decision"
  | "meal_entry"
  | "body_measurement"
  | "daily_intent"
  | "nutrition_target"
  | "notification_settings"
  | "training_template"
  | "device";

/** 事件载荷（按实体类型对应的领域实体）。 */
export type SyncEntityPayload =
  | UserProfile
  | InjuryConstraint
  | WorkoutPlan
  | WorkoutSession
  | WorkoutExercise
  | WorkoutSet
  | ReadinessCheck
  | RecoveryCheck
  | ProgressionDecision
  | MealEntry
  | BodyMeasurement
  | DailyIntent
  | NotificationSetting
  | TrainingTemplate;

/** 同步事件（客户端生成；id 即幂等键，§25.2）。 */
export interface SyncEventEnvelope {
  /** 客户端生成 UUID——同时也是幂等键（服务端唯一约束，重复推送不重复建记录）。 */
  id: string;
  userId: string;
  deviceId: string;
  entityType: SyncEntityType;
  entityId: string;
  operation: "upsert" | "delete";
  entityVersion: number;
  payload: SyncEntityPayload;
  clientCreatedAt: string;
}

/** 服务端/客户端共用的实体存储键。 */
export function entityKey(entityType: SyncEntityType, entityId: string): string {
  return `${entityType}:${entityId}`;
}

/** 增量游标（服务端单调递增；客户端保存 lastSyncCursor）。 */
export type SyncCursor = number;

/** 冲突记录（§25.3：所有冲突留下日志）。 */
export interface ConflictRecord {
  eventId: string; // 后到事件
  previousEventId: string | null;
  entityType: SyncEntityType;
  entityId: string;
  resolution:
    | "keep_both" // 训练组双版本保留（workout_set）
    | "lww" // 设置类：最新修改优先
    | "stricter_wins" // 伤情：较严格优先（绝不放宽）
    | "corrected_wins" // 饮食：用户修正优先
    | "completed_wins"; // 训练：已完成不回退
  reasonCodes: string[];
  createdAt: string;
}

/** 服务端同步状态（纯内存形态；持久化由宿主负责——Edge Function/SQL）。 */
export interface ServerState {
  /** entityKey → 实体（应用后的权威值；workout_set 冲突时另一版本进 extraVersions） */
  entities: Map<string, SyncEntityPayload>;
  /** entityKey → 实体版本号（用于冲突检测与增量） */
  versions: Map<string, number>;
  /** entityKey → 冲突/替换保留的额外版本（key = `${entityKey}#v${version}`） */
  extraVersions: Map<string, SyncEntityPayload>;
  /** 已应用事件 id 集合（幂等键去重） */
  appliedEventIds: Set<string>;
  /** 增量日志（按应用顺序递增 seq：事件 + 权威值 + extras + 冲突，供客户端收敛与审计） */
  deltaLog: Array<{
    seq: number;
    event: SyncEventEnvelope;
    authority: SyncEntityPayload | null;
    extras: SyncEntityPayload[];
    conflict: ConflictRecord | null;
  }>;
  /** 全部冲突记录（供审计/客户端展示） */
  conflicts: ConflictRecord[];
  /** 下一事件序号（游标水位） */
  cursor: number;
}

export function createServerState(): ServerState {
  return {
    entities: new Map(),
    versions: new Map(),
    extraVersions: new Map(),
    appliedEventIds: new Set(),
    deltaLog: [],
    conflicts: [],
    cursor: 0,
  };
}